import * as Sentry from "@sentry/react";

export interface ErrorTrackingConfig {
  /** GlitchTip project DSN; empty or undefined means "off". */
  dsn?: string;
  /** Which installation this is (local, prod) - a tag on every event. */
  environment?: string;
  /** The commit the image was built from, so an issue says which version threw. */
  release?: string;
  /** Which application, for the `app` tag; the DSN already decides the project, this survives a copy-paste. */
  app: string;
}

let enabled = false;

/** Turns the SDK on when a DSN is configured. Call once, before the first render. */
export function initErrorTracking(config: ErrorTrackingConfig): boolean {
  if (!config.dsn) {
    return false;
  }
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? "local",
    release: config.release && config.release !== "dev" ? config.release : undefined,
    sendDefaultPii: false,
    // Errors only: no performance spans, no replay, no user feedback widget.
    tracesSampleRate: 0,
    integrations: (defaults) => defaults.filter((integration) => integration.name !== "BrowserSession"),
    // Noise every SPA sees and nobody can fix from here.
    ignoreErrors: [
      "ResizeObserver loop",
      "Failed to fetch dynamically imported module",
      "Importing a module script failed",
      /Loading chunk \d+ failed/,
      "AbortError",
      "Load failed",
    ],
    beforeSend(event) {
      // Tokens travel in the fragment after the OIDC redirect and in `?code=&state=`; neither belongs in an issue.
      if (event.request?.url) {
        event.request.url = scrub(event.request.url);
      }
      for (const breadcrumb of event.breadcrumbs ?? []) {
        if (typeof breadcrumb.data?.url === "string") {
          breadcrumb.data.url = scrub(breadcrumb.data.url);
        }
      }
      return event;
    },
  });
  Sentry.setTag("app", config.app);
  enabled = true;
  return true;
}

/** The signed-in person as an opaque id (the Keycloak subject), never an e-mail; `null` on sign-out. */
export function setErrorTrackingUser(id: string | null, tenant?: string | null): void {
  if (!enabled) {
    return;
  }
  Sentry.setUser(id ? { id } : null);
  Sentry.setTag("tenant", tenant ?? undefined);
}

/** For code that catches and recovers but still wants the failure counted (a failed offline sync, say). */
export function captureException(error: unknown, context?: Record<string, string | number | boolean>): string | undefined {
  if (!enabled) {
    return undefined;
  }
  return Sentry.captureException(error, context ? { tags: context } : undefined);
}

function scrub(url: string): string {
  return url.replace(/([?&#])(code|state|token|access_token|id_token|session_state)=[^&#]*/gi, "$1$2=***");
}
